import styled from "styled-components";
import { Link } from "../../components/atoms/Link";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import { SubHeading } from "../../components/atoms/Text";
import CompactTable, {
  leftColumnPrimaryStyle,
  leftColumnSecondaryStyle,
} from "../../components/organisms/CompactTable";
import colours from "../../lib/colours";
import Callout from "../../components/molecules/Callout";
import png1 from "../../assets/images/map-removal1.png";
import png2 from "../../assets/images/map-removal2.png";
import png3 from "../../assets/images/map-removal3.png";
import png4 from "../../assets/images/map-removal4.png";
import png5 from "../../assets/images/map-removal5.png";
import png6 from "../../assets/images/map-removal6.png";
import png7 from "../../assets/images/map-removal7.png";
import { applicantsGuide, assessorsHandbook } from "../../lib/constants";

const Container = styled.div`
  display: block;
  font-weight: 300;
`;

const ContentSubHeading = styled(SubHeading)`
  font-size: 17px;
`;

const MediumSpan = styled.span`
  font-weight: 500;
`;

const TableTextContainer = styled.div`
  color: ${colours.darkerGrey};
  font-weight: 300;
  font-size: 16px;
`;

const NoMarginP = styled.p`
  margin: 0;
`;

const ItalicP = styled.p`
  font-style: italic;
`;

const Span16 = styled.span`
  font-size: 16px;
`;

const TableTwoColumnContainer = styled(TableTextContainer)`
  display: flex;
`;

const StyledImage = styled.img`
  min-width: 40%;
  max-width: 40%;
  object-fit: cover;
`;

const SpacedLi = styled.li`
  margin-block: 8px;
`;

const InsetDiv = styled.div`
  padding-left: 22px;
`;

const twoColumnContentStyle: any = { ...leftColumnSecondaryStyle };
twoColumnContentStyle.padding = 0;
twoColumnContentStyle.paddingLeft = 0;
twoColumnContentStyle.paddingBlock = "18px 36px";

const patchDescription = (
  <TableTextContainer>
    <NoMarginP>
      A <MediumSpan>Patch</MediumSpan> can consist of one, or a combination of
      the following:
    </NoMarginP>
    <ul>
      <li>
        An area of vegetation where native plants make up at least 25% of the
        total perennial
        <MediumSpan style={{ color: colours.th1l3alt }}>*</MediumSpan>{" "}
        understorey plant cover.
      </li>
      <li>
        Any area that contains three or more{" "}
        <MediumSpan>Canopy Trees</MediumSpan> with overlapping driplines.
      </li>
      <li>
        An area located within a Mapped Wetland, irrespective of the presence of
        native vegetation during assessment.
      </li>
    </ul>
    <p>Notes:</p>
    <ItalicP>
      <MediumSpan style={{ color: colours.th1l3alt }}>*</MediumSpan> Perennial
      plants have a lifespan of over two years and encompass species that may or
      may not be consistently visible above the ground.
    </ItalicP>
    <Callout type="info" backgroundColor={colours.offWhite}>
      An example of identifying areas where at least 25% of the perennial
      understorey plant cover is native:
      <br />
      <br />
      An area is composed of 80% weedy annual grasses, 15% native perennial
      plants, and 5% weedy perennial plants. Disregarding the cover provided by
      the annual weeds (80%), the remaining plant cover consists of perennial
      plants, accounting for 20%. Within this perennial plant cover, 75% (15/20)
      comprises native species, indicating the presence of a Patch.
    </Callout>
    <br />
  </TableTextContainer>
);

const canopyTreeDescription = (
  <TableTextContainer>
    <NoMarginP>
      A <MediumSpan>Canopy Tree</MediumSpan> refers to a mature tree, exceeding
      a height of 3 meters, capable of flowering, and commonly found in the
      upper layer of the respective vegetation type. These trees can be present
      within a <MediumSpan>Patch</MediumSpan> of native vegetation (referred to
      as <MediumSpan>Patch Tree</MediumSpan>) or occur independently (known as a{" "}
      <MediumSpan>Scattered Tree</MediumSpan>). Additionally, a{" "}
      <MediumSpan>Canopy Tree</MediumSpan> can also encompass a dead tree with a
      diameter of 40 cm or larger.
    </NoMarginP>
  </TableTextContainer>
);

const scenariosContent = [
  <div>
    <ul>
      <SpacedLi>
        An area of vegetation where native plants make up at least 25% of the
        total perennial understorey plant cover.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area of vegetation where native plants make up at least 25% of the
        total perennial understorey plant cover.
      </SpacedLi>
      <SpacedLi>
        Patch Trees are present and wholly contained within the mapped Patch.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area of vegetation where native plants make up at least 25% of the
        total perennial understorey plant cover.
      </SpacedLi>
      <SpacedLi>
        The canopies of some Patch Trees extend beyond the understorey Patch
        boundaries.
      </SpacedLi>
      <SpacedLi>
        The Patch boundary must encompass the canopy/drip line of any impacted
        Patch Trees.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area with three or more Canopy Trees where the drip line of each tree
        touches the drip line of at least one other tree, forming a continuous
        canopy cover.
      </SpacedLi>
      <SpacedLi>
        The total perennial understorey plant cover is &lt; 25% native.
      </SpacedLi>
      <SpacedLi>
        The boundary of the Patch must encompass the canopy/drip line of the
        trees.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area within the Current Wetland layer indicated in NVR Map.
      </SpacedLi>
      <SpacedLi>
        The Current Wetland layer represents areas where native vegetation
        associated with wetlands may not be visible or may be in a degraded
        state during assessment.
      </SpacedLi>
      <SpacedLi>
        Mapped Wetland areas can be excluded from consideration if they are
        covered by a hardened surface or under other circumstances with approval
        from the Secretary to DEECA.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>A Scattered Tree(s) proposed for removal.</SpacedLi>
      <SpacedLi>
        The total perennial understorey plant cover is &lt; 25% native.
      </SpacedLi>
      <SpacedLi>
        Includes dead trees with a diameter of 40cm or greater.
      </SpacedLi>
    </ul>
    <InsetDiv>
      <p style={{ marginBottom: 0 }}>Notes:</p>
      <ItalicP style={{ marginTop: "6px" }}>
        The polygon extends beyond the dripline of the tree(s). The area of the
        polygon, rather than the individual tree(s), is used to calculate the
        loss to biodiversity values.
      </ItalicP>
    </InsetDiv>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        A Patch Tree proposed for removal, with the native understorey
        vegetation to be retained (i.e. partial removal).
      </SpacedLi>
      <SpacedLi>
        Includes dead trees with a diameter of 40cm or greater.
      </SpacedLi>
    </ul>
  </div>,
];

const ScenarioLayout = ({
  imgSource,
  imgAlt,
  children,
}: {
  imgSource: any;
  imgAlt: string;
  children: any;
}) => {
  return (
    <TableTwoColumnContainer>
      <StyledImage src={imgSource} alt={imgAlt} title={imgAlt} />
      {children}
    </TableTwoColumnContainer>
  );
};

function HowToMapRemoval() {
  const definitionRows: any = [
    {
      rowData: [<Span16 key={0}>Patch</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [<span key={1}>{patchDescription}</span>],
      rowStyles: [leftColumnSecondaryStyle],
    },
    {
      rowData: [
        <Span16 key={2}>Canopy Tree (Patch Tree and Scattered Tree)</Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [<span key={3}>{canopyTreeDescription}</span>],
      rowStyles: [leftColumnSecondaryStyle],
    },
  ];

  const scenarioRows: any = [
    {
      rowData: [<Span16 key={0}>1. Patch without Patch Trees</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={1}
          imgSource={png1}
          imgAlt="Example of Patch without Patch Trees"
        >
          {scenariosContent[0]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [<Span16 key={2}>2. Patch with Patch Trees</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={3}
          imgSource={png2}
          imgAlt="Example of Patch with Patch Trees"
        >
          {scenariosContent[1]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [
        <Span16 key={4}>
          3. Patch with Patch Trees overhanging the edge of the Patch
        </Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={5}
          imgSource={png3}
          imgAlt="Example of Patch with Patch Trees overhanging the edge of the Patch"
        >
          {scenariosContent[2]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [
        <Span16 key={6}>
          4. Patch without native understorey (3 or more trees only)
        </Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={7}
          imgSource={png4}
          imgAlt="Example of Patch without native understorey"
        >
          {scenariosContent[3]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [<Span16 key={8}>5. Patch including Mapped Wetland</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={9}
          imgSource={png5}
          imgAlt="Example of Patch including Mapped Wetland"
        >
          {scenariosContent[4]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [<Span16 key={10}>6. Scattered Tree(s)</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={11}
          imgSource={png6}
          imgAlt="Example of Scattered Trees"
        >
          {scenariosContent[5]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [
        <Span16 key={12}>7. Patch Tree only (no understorey removal)</Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={13}
          imgSource={png7}
          imgAlt="Example of Patch Tree only (no understorey removal)"
        >
          {scenariosContent[6]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
  ];

  return (
    <Container>
      <p>
        For detailed information on how to define and map native vegetation,
        refer to the following resources:
      </p>
      <ul>
        <li>
          <Link
            href={applicantsGuide}
            target="_blank"
            title="Applicant’s Guide"
            rel="noopener noreferrer"
          >
            Applicant’s Guide
          </Link>
          <ExternalLinkIcon /> – Section 3 provides valuable information.
        </li>
        <li>
          <Link
            href={assessorsHandbook}
            target="_blank"
            title="Assessor’s Handbook"
            rel="noopener noreferrer"
          >
            Assessor’s Handbook
          </Link>
          <ExternalLinkIcon /> – Appendices 1 and 6 offer useful guidance.
        </li>
      </ul>
      <p>
        The images below highlight typical scenarios of native vegetation
        removal. Your proposal can encompass any single scenario or a
        combination thereof. It is important to accurately map the total extent
        of impacted Patches of native vegetation and Canopy Trees, which may
        include:
      </p>
      <ul>
        <li>
          <MediumSpan>Direct removal</MediumSpan> – Native vegetation that you
          intend to physically remove.
        </li>
        <li>
          <MediumSpan>Assumed loss</MediumSpan> – Native vegetation that will
          likely degrade or perish due to the proposed works, even though it
          will not be physically removed. This category covers vegetation
          affected by encroachment into Tree Protection Zones (TPZ), changed
          water flows, compaction, or shading.
        </li>
        <li>
          <MediumSpan>Consequential loss</MediumSpan> – Native vegetation that
          will not be physically removed but would become subject to an
          exemption because of the proposed approval. This includes, but is not
          limited to, future allowable losses under the Site Area Exemption
          (subdivisions creating lots &lt; 0.2ha), Fire Protection Exemption
          (approval of dwellings requiring defendable space), and Fences
          Exemption (approval of fences requiring future maintenance).
        </li>
      </ul>
      <div style={{ marginTop: "36px" }}>
        <ContentSubHeading>Definitions</ContentSubHeading>
        <CompactTable
          rows={definitionRows}
          getRowKey={(row: any) => row[0].key}
        ></CompactTable>
        <ContentSubHeading>
          Common native vegetation removal scenarios
        </ContentSubHeading>
        <CompactTable
          rows={scenarioRows}
          getRowKey={(row: any) => row[0].key}
        ></CompactTable>
      </div>
    </Container>
  );
}

export default HowToMapRemoval;
